import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import PrintRounded from "@mui/icons-material/PrintRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import ZoomInRounded from "@mui/icons-material/ZoomInRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { LoadingButton } from "@mui/lab";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import jsPDF from "jspdf";

import { DefaultPage } from "../../../layouts/default-page";
import { StatusChip } from "../../../components/common/StatusChip";
import { Notify } from "../../../layouts/notify";
import ImageFunctions from "../../../components/Functions/PdfImageFunctions";
import { compressImage } from "../../../utils/image-compression";
import {
  useGetDeputyReviewDetailsQuery,
  useUpdateDeputyReviewStatusMutation,
} from "../../../services/deputy-reviews.query";

// ---- strict status union & helper ----
const REVIEW_STATUSES = ["ACKNOWLEDGED", "DUPLICATE", "REJECTED"] as const;
type ReviewStatus = (typeof REVIEW_STATUSES)[number];

const toReviewStatus = (s: string): ReviewStatus => {
  const u = s.toUpperCase();
  if ((REVIEW_STATUSES as readonly string[]).includes(u)) {
    return u as ReviewStatus;
  }
  return "ACKNOWLEDGED";
};

export default function MtrcbMonitoringMovieDetails() {
  const imageLoader = new ImageFunctions();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const deputyReview = location.state?.deputyReview;
  const reportId = params.id || searchParams.get("id") || (deputyReview?.id ? String(deputyReview.id) : undefined);
  const lUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [printLoading, setPrintLoading] = useState(false);
  const [status, setStatus] = useState<string>("acknowledged");
  const [adminComment, setAdminComment] = useState<string>("");
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  const handleBack = () => {
    const returnUrl = location.state?.returnUrl || sessionStorage.getItem("last_movies_url");
    if (returnUrl) {
      navigate(returnUrl);
    } else {
      navigate("/mtrcb-monitoring/movies");
    }
  };

  const {
    data: DeputyReviewDetails,
    isLoading,
    isError,
    refetch,
  } = useGetDeputyReviewDetailsQuery(reportId as any, {
    skip: !reportId,
  });

  const [updateStatus, { isLoading: updating }] =
    useUpdateDeputyReviewStatusMutation();

  const details = DeputyReviewDetails || deputyReview;

  useEffect(() => {
    if (!details) return;

    const s = String(details.status || "").toUpperCase();
    if (["ACKNOWLEDGED", "DUPLICATE", "REJECTED"].includes(s)) {
      setStatus(s.toLowerCase());
    } else {
      setStatus("acknowledged");
    }

    if (details.admin_comment) {
      setAdminComment(details.admin_comment);
    }
  }, [details]);

  const handleStatusChange = (e: SelectChangeEvent<string>) => {
    setStatus(e.target.value);
  };

  const handleUpdateStatusAndRemarks = async () => {
    if (!details?.id) return;
    try {
      await updateStatus({
        id: details.id,
        status: toReviewStatus(status),
        user_id: lUser.user_id,
        admin_comment: adminComment.trim(),
      }).unwrap();

      if (sendEmail && details.deputy?.email) {
        try {
          const deputy = details.deputy;
          const payload = {
            recipient: deputy.email,
            deputyId: deputy.deputy_control_number ?? "",
            firstName: deputy.name ?? "",
            lastName: deputy.last_name ?? "",
            status: toReviewStatus(status),
            comment: adminComment.trim(),
            reportId: details.id,
            reportType: "Movie",
            withViolation: !!details.has_violation,
            description: details.other_comment ?? null,
            dateTime: new Date(
              details.ticket_request?.movie_schedule_datetime ?? details.created_at
            ).toISOString(),
          };

          const resp = await fetch(
            "https://mtrcb-app-server.vercel.app/api/email/send/report-comment",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );

          const json = await resp.json().catch(() => ({}));
          if (!resp.ok || json?.success !== true) {
            Notify("warning", "Status saved, but email notification failed to send.");
          } else {
            Notify("success", "Status updated & personnel notified via email.");
          }
        } catch {
          Notify("warning", "Status saved, but email notification failed to send.");
        }
      } else {
        Notify("success", "Monitoring report status updated successfully.");
      }

      refetch?.();
    } catch (err: any) {
      console.error("Status update error:", err);
      Notify("error", err?.message || "Failed to update report status.");
    }
  };

  const handlePrintPDF = async (data: any) => {
    if (!data) return;
    setPrintLoading(true);
    const doc = new jsPDF();

    try {
      const bannerImageBase64 = await imageLoader.loadLocalImageAsBase64(
        `${process.env.PUBLIC_URL}/pdf-banner.jpg`
      );

      doc.addImage(
        bannerImageBase64,
        "PNG",
        14,
        8,
        182,
        35,
        undefined,
        "FAST"
      );

      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      doc.text("MTRCB Monitoring Movie Report", 14, 50);

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text("Report Reference ID:", 14, 56);
      doc.setTextColor(0, 0, 0);
      doc.text(String(data.id ?? "-"), 65, 56);

      doc.setTextColor(100, 100, 100);
      doc.text("Date and Time of Report:", 14, 62);
      doc.setTextColor(0, 0, 0);
      doc.text(
        data?.created_at
          ? String(format(new Date(data.created_at), "MMMM dd, yyyy · hh:mm a"))
          : "-",
        65,
        62
      );

      doc.setFontSize(12);
      doc.setTextColor(0, 114, 188);
      doc.text("Personnel Inspector Info:", 14, 76);
      doc.setFontSize(10);

      const startX = 14;
      let currentY = 82;
      const lineHeight = 5;
      const valueOffsetX = 65;

      const PAGE_BOTTOM = 280;
      const MARGIN_TOP = 20;
      const QUESTION_SPACING = 2;
      const ANSWER_SPACING = 5;
      const BLOCK_SPACING = 3;
      const ANSWER_INDENT = 8;
      const COMMENT_INDENT = 5;
      const MAX_WIDTH = 180;
      const COMMENT_WIDTH = 172;

      const ensurePageSpace = (y: number, neededHeight = 0) => {
        if (y + neededHeight > PAGE_BOTTOM) {
          doc.addPage();
          return MARGIN_TOP;
        }
        return y;
      };

      const addWrappedText = (
        text: string,
        x: number,
        y: number,
        maxWidth = MAX_WIDTH
      ) => {
        const safeText = String(text ?? "");
        const lines = doc.splitTextToSize(safeText, maxWidth);
        for (const line of lines) {
          y = ensurePageSpace(y, lineHeight);
          doc.text(String(line), x, y);
          y += lineHeight;
        }
        return y + QUESTION_SPACING;
      };

      const addAnswer = (answer: string, x: number, y: number) => {
        y = ensurePageSpace(y, lineHeight);
        const safeAnswer = String(answer ?? "-");
        const normalized = safeAnswer.trim().toLowerCase();
        if (normalized === "yes") {
          doc.setTextColor(0, 102, 204);
          doc.setFont("", "bold");
        } else if (normalized === "no") {
          doc.setTextColor(220, 0, 0);
          doc.setFont("", "bold");
        } else {
          doc.setTextColor(60, 60, 60);
          doc.setFont("", "normal");
        }
        doc.text(safeAnswer, x, y);
        doc.setTextColor(0, 0, 0);
        doc.setFont("", "normal");
        return y + ANSWER_SPACING;
      };

      const deputyInfo = [
        {
          label: "Name:",
          value: String(
            `${data.deputy?.name || ""} ${data.deputy?.last_name || ""}`.trim() ||
              "-"
          ),
        },
        { label: "Email:", value: String(data.deputy?.email || "-") },
        { label: "Contact No:", value: String(data.deputy?.phone_number || "-") },
        { label: "Role:", value: String(data.deputy?.role || "MTRCB") },
      ];

      deputyInfo.forEach((info) => {
        currentY = ensurePageSpace(currentY, lineHeight);
        doc.setTextColor(100, 100, 100);
        doc.text(String(info.label), startX, currentY);
        doc.setTextColor(0, 0, 0);
        doc.text(String(info.value ?? "-"), valueOffsetX, currentY);
        currentY += lineHeight;
      });

      doc.setFontSize(12);
      currentY += 4;
      currentY = ensurePageSpace(currentY, 10);
      doc.setTextColor(0, 114, 188);
      doc.text("Exhibition & Movie Details:", 14, currentY);
      currentY += 6;
      doc.setFontSize(10);

      const movieDetails = [
        {
          label: "Movie Title:",
          value: String(data.ticket_request?.movie_title || "-"),
        },
        {
          label: "Ticket Code:",
          value: String(data.ticket_request?.code || "-"),
        },
        {
          label: "Mall:",
          value: String(data.ticket_request?.theatre_group?.name || "-"),
        },
        {
          label: "Cinema:",
          value: String(data.ticket_request?.cinema_name || "-"),
        },
        {
          label: "Movie Schedule:",
          value: String(
            data.ticket_request?.movie_schedule_datetime
              ? format(
                  new Date(data.ticket_request.movie_schedule_datetime),
                  "MMMM dd, yyyy · hh:mm a"
                )
              : "-"
          ),
        },
        {
          label: "Accompanied By:",
          value: String(
            `${data.ticket_request?.comany_name || ""} ${
              data.ticket_request?.company_lastname || ""
            }`.trim() || "None"
          ),
        },
        {
          label: "Concerned Personnel:",
          value: String(data.concerned_personnel_name || "None"),
        },
        {
          label: "Violation Tag:",
          value: data.has_violation ? "With Violation" : "No Violation (Compliant)",
        },
        {
          label: "Status:",
          value: String(data.status || "-"),
        },
      ];

      movieDetails.forEach((detail) => {
        currentY = ensurePageSpace(currentY, lineHeight);
        doc.setTextColor(100, 100, 100);
        doc.text(String(detail.label), startX, currentY);
        doc.setTextColor(0, 0, 0);
        doc.text(String(detail.value ?? "-"), valueOffsetX, currentY);
        currentY += lineHeight;
      });

      doc.setFontSize(12);
      currentY += 4;
      currentY = ensurePageSpace(currentY, 10);
      doc.setTextColor(0, 114, 188);
      doc.text("10-Point Inspection Criteria:", 14, currentY);
      currentY += 6;
      doc.setFontSize(10);

      const reportQuestions = [
        "1. The cinema personnel verified the Personnel ID and pass.",
        "2. Permit to Exhibit is posted at a conspicuous place in the ticket-selling area.",
        "3. Permit to Exhibit displays the correct classification rating.",
        "4. Movie being shown is within the validity period for exhibition stated in the Permit.",
        "5. The classification rating in the Permit matches all promotional materials.",
        "6. Theater strictly complies with age-suitability admission rules.",
        "7. Posted correct classification standee at or near the theater's lobby or entrance.",
        "8. Promotional materials contain no inappropriate content.",
        "9. Trailer ratings are appropriate for the featured film.",
        "10. Advisory announcing the film classification is shown prior to the movie.",
        "Personnel Observations & Comments:",
      ];

      reportQuestions.forEach((text, index) => {
        doc.setTextColor(60, 60, 60);
        currentY = addWrappedText(text, startX, currentY, MAX_WIDTH);

        let val = "";
        switch (index) {
          case 0:
            val = data.question_one ? "Yes" : "No";
            break;
          case 1:
            val = data.question_two ? "Yes" : "No";
            break;
          case 2:
            val = data.question_three ? "Yes" : "No";
            break;
          case 3:
            val = data.question_four ? "Yes" : "No";
            break;
          case 4:
            val = data.question_five ? "Yes" : "No";
            break;
          case 5:
            val = data.question_six ? "Yes" : "No";
            break;
          case 6:
            val = data.question_seven ? "Yes" : "No";
            break;
          case 7:
            val = data.question_eight ? "Yes" : "No";
            break;
          case 8:
            val = data.question_nine ? "Yes" : "No";
            break;
          case 9:
            val = data.question_ten ? "Yes" : "No";
            break;
          case 10:
            val = String(data.other_comment ?? data.comments ?? "-");
            break;
          default:
            val = "";
        }

        if (index === 10) {
          doc.setTextColor(0, 0, 0);
          currentY = addWrappedText(
            val || "-",
            startX + COMMENT_INDENT,
            currentY,
            COMMENT_WIDTH
          );
          currentY += BLOCK_SPACING;
        } else {
          currentY = addAnswer(val, startX + ANSWER_INDENT, currentY);
          currentY += BLOCK_SPACING;
        }
      });

      doc.setFontSize(12);
      currentY += 4;
      currentY = ensurePageSpace(currentY, 10);
      doc.setTextColor(0, 114, 188);
      doc.text("Administrative Evaluation:", 14, currentY);
      currentY += 6;
      doc.setFontSize(10);

      const addInfo = [
        {
          label: "Evaluated By:",
          value: data.admin
            ? String(`${data.admin.name || ""} ${data.admin.last_name || ""}`.trim())
            : "Pending Evaluation",
        },
        { label: "Status:", value: String(data.status || "PENDING") },
        { label: "Remarks / Notes:", value: String(data.admin_comment || "None") },
      ];

      addInfo.forEach((info) => {
        currentY = ensurePageSpace(currentY, lineHeight);
        doc.setTextColor(100, 100, 100);
        doc.text(String(info.label), startX, currentY);
        doc.setTextColor(0, 0, 0);
        doc.text(String(info.value ?? "-"), valueOffsetX, currentY);
        currentY += lineHeight;
      });

      const imageAttachments = (data.attachments || []).filter(
        (attachment: any) =>
          attachment.type === "IMAGE" ||
          attachment.file_url?.match(/\.(jpeg|jpg|png|webp)/i)
      );

      if (imageAttachments.length > 0) {
        let attachmentsStartY = currentY + 8;
        attachmentsStartY = ensurePageSpace(attachmentsStartY, 10);

        doc.setFontSize(12);
        doc.setTextColor(0, 114, 188);
        doc.text("Image Attachments:", 14, attachmentsStartY);
        attachmentsStartY += 8;

        const maxImageWidth = 40;
        const padding = 5;
        const columnCount = 2;
        let currentRowHeight = 0;
        let currentX = startX;
        currentY = attachmentsStartY;

        for (const [index, attachment] of imageAttachments.entries()) {
          const imgURL = attachment.file_url;
          const imageFormat = imageLoader.getImageFormat(imgURL) || "JPEG";

          try {
            const response = await fetch(imgURL);
            const imageBlob = await response.blob();

            const compressedBlob = await compressImage(imageBlob, {
              maxSizeMB: 0.2,
              maxWidthOrHeight: 500,
              useWebWorker: true,
            });

            const reader = new FileReader();
            reader.readAsDataURL(compressedBlob);
            await new Promise((resolve) => {
              reader.onloadend = resolve;
            });

            const compressedBase64 = reader.result as string;

            const img = new Image();
            img.src = compressedBase64;

            await new Promise((resolve) => {
              img.onload = () => resolve(null);
            });

            const { width, height } = img;
            let newWidth = maxImageWidth;
            let newHeight = (height / width) * maxImageWidth;

            if (newHeight > maxImageWidth) {
              newHeight = maxImageWidth;
              newWidth = (width / height) * maxImageWidth;
            }

            if (index % columnCount === 0 && index !== 0) {
              currentY += currentRowHeight + padding;
              currentX = startX;
              currentRowHeight = 0;
            }

            currentY = ensurePageSpace(currentY, newHeight + padding);

            doc.addImage(
              compressedBase64,
              imageFormat,
              currentX,
              currentY,
              newWidth,
              newHeight
            );

            currentRowHeight = Math.max(currentRowHeight, newHeight);
            currentX += maxImageWidth + padding;
          } catch (error) {
            console.error("Error compressing image for PDF:", error);
          }
        }
      }

      doc.save(`MTRCB_Monitoring_Report_${String(data.id)}.pdf`);
      Notify("success", "Monitoring report PDF generated successfully.");
    } catch (error) {
      console.error("Error generating PDF:", error);
      Notify("error", "Failed to generate report PDF.");
    } finally {
      setPrintLoading(false);
    }
  };

  const reviewQuestions = [
    {
      label: "1. The cinema personnel verified the Personnel ID and pass.",
      key: "question_one",
    },
    {
      label: "2. Permit to Exhibit is posted at a conspicuous place in the ticket-selling area.",
      key: "question_two",
    },
    {
      label: "3. Permit to Exhibit displays the correct classification rating.",
      key: "question_three",
    },
    {
      label: "4. Movie being shown is within the validity period for exhibition stated in the Permit.",
      key: "question_four",
    },
    {
      label: "5. The classification rating in the Permit matches all promotional materials.",
      key: "question_five",
    },
    {
      label: "6. Theater strictly complies with age-suitability admission rules.",
      key: "question_six",
    },
    {
      label: "7. Posted correct classification standee at or near the theater's lobby or entrance.",
      key: "question_seven",
    },
    {
      label: "8. Promotional materials contain no inappropriate content.",
      key: "question_eight",
    },
    {
      label: "9. Trailer ratings are appropriate for the featured film.",
      key: "question_nine",
    },
    {
      label: "10. Advisory announcing the film classification is shown prior to the movie.",
      key: "question_ten",
    },
  ];

  if (isLoading && !details) {
    return (
      <DefaultPage title="MTRCB Monitoring Movie Report Details" subTitle="Loading report details...">
        <Box sx={{ p: 3, width: "100%" }}>
          <Skeleton variant="rectangular" height={50} sx={{ borderRadius: "6px", mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: "6px", mb: 2 }} />
              <Skeleton variant="rectangular" height={260} sx={{ borderRadius: "6px" }} />
            </Grid>
            <Grid item xs={12} lg={5}>
              <Skeleton variant="rectangular" height={360} sx={{ borderRadius: "6px" }} />
            </Grid>
          </Grid>
        </Box>
      </DefaultPage>
    );
  }

  if (!details) {
    return (
      <DefaultPage title="MTRCB Monitoring Movie Report" subTitle="Report not found">
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="error" variant="h6" mb={2}>
            {isError ? "Failed to load monitoring report details." : "Report not found or was removed."}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackRounded />}
            onClick={handleBack}
            sx={{ backgroundColor: "#0072BC" }}
          >
            Back to MTRCB Reports
          </Button>
        </Box>
      </DefaultPage>
    );
  }

  const deputy = details.deputy || {};
  const ticket = details.ticket_request || {};
  const attachments = details.attachments || [];
  const imageAttachments = attachments.filter((a: any) => a.type === "IMAGE" || a.file_url?.match(/\.(jpeg|jpg|png|webp)/i));
  const videoAttachments = attachments.filter((a: any) => a.type === "VIDEO" || a.file_url?.match(/\.(mp4|mov|webm)/i));

  return (
    <DefaultPage
      title="MTRCB Personnel Movie Report Details"
      subTitle="Review, evaluate, and moderate cinema monitoring reports submitted by MTRCB Personnel."
    >
      {/* Top Navigation & Action Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackRounded fontSize="small" />}
          onClick={handleBack}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "4px",
            borderColor: "#CBD5E1",
            color: "#475569",
            "&:hover": {
              borderColor: "#94A3B8",
              backgroundColor: "#F8FAFC",
            },
          }}
        >
          Back to MTRCB Reports
        </Button>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <StatusChip label={details.status || "PENDING"} />
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintRounded fontSize="small" />}
            onClick={() => handlePrintPDF(details)}
            disabled={printLoading}
            sx={{
              textTransform: "none",
              fontWeight: 650,
              borderRadius: "4px",
              backgroundColor: "#0072BC",
              "&:hover": { backgroundColor: "#005a96" },
              boxShadow: "none",
            }}
          >
            {printLoading ? "Generating..." : "Print PDF"}
          </Button>
        </Stack>
      </Box>

      {/* Main Grid Content */}
      <Grid container spacing={3}>
        {/* Left Column: Details & Evidence */}
        <Grid item xs={12} lg={7}>
          <Stack spacing={3}>
            {/* Personnel Inspector Information Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  Personnel Inspector Information
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                  <Avatar
                    src={deputy.profile_picture}
                    alt={deputy.name}
                    sx={{
                      width: 54,
                      height: 54,
                      border: "2px solid #E2E8F0",
                    }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1E293B", lineHeight: 1.2 }}>
                      {deputy.name} {deputy.last_name}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "#64748B", mt: 0.25 }}>
                      MTRCB Staff / Personnel · {deputy.role || "MTRCB"}
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Email Address
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: "#1E293B", fontWeight: 500 }}>
                      {deputy.email || "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Contact Number
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: "#1E293B", fontWeight: 500 }}>
                      {deputy.phone_number || "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Date & Time Reported
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: "#1E293B", fontWeight: 500 }}>
                      {details.created_at
                        ? format(new Date(details.created_at), "MMM dd, yyyy · hh:mm a")
                        : "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Report Reference ID
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0072BC" }}>
                      #{details.id}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Theatre & Exhibition Details Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  Theatre & Exhibition Details
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Movie Title
                    </Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1E293B" }}>
                      {ticket.movie_title || details.movie_title || "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Violation Assessment
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1,
                          py: 0.25,
                          borderRadius: "4px",
                          fontSize: 12,
                          fontWeight: 700,
                          backgroundColor: details.has_violation ? "#FEE2E2" : "#DCFCE7",
                          color: details.has_violation ? "#DC2626" : "#16A34A",
                        }}
                      >
                        {details.has_violation ? (
                          <>
                            <WarningAmberRounded sx={{ fontSize: 15 }} />
                            <span>WITH VIOLATION</span>
                          </>
                        ) : (
                          <>
                            <CheckCircleOutlineRounded sx={{ fontSize: 15 }} />
                            <span>NO VIOLATION (COMPLIANT)</span>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Mall / Theatre Group
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1E293B" }}>
                      {ticket.theatre_group?.name || "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Cinema Hall
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1E293B" }}>
                      {ticket.cinema_name || "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Screening Date & Time
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: "#1E293B", fontWeight: 500 }}>
                      {ticket.movie_schedule_datetime
                        ? format(new Date(ticket.movie_schedule_datetime), "MMMM dd, yyyy · hh:mm a")
                        : "—"}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* 10-Point Inspection Criteria Checklist */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  10-Point Inspection Criteria Checklist
                </Typography>

                <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                  {reviewQuestions.map((q, idx) => {
                    const isYes = Boolean(details[q.key]);
                    return (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 2,
                          p: 1.25,
                          borderRadius: "4px",
                          backgroundColor: idx % 2 === 0 ? "#F8FAFC" : "#FFFFFF",
                          border: "1px solid #F1F5F9",
                        }}
                      >
                        <Typography sx={{ fontSize: 13, color: "#334155", fontWeight: 500, flex: 1 }}>
                          {q.label}
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: "3px",
                            fontSize: 11.5,
                            fontWeight: 700,
                            letterSpacing: "0.03em",
                            backgroundColor: isYes ? "#DCFCE7" : "#FEE2E2",
                            color: isYes ? "#16A34A" : "#DC2626",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isYes ? "✓ YES" : "✕ NO"}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>

                <Divider sx={{ mb: 2, borderColor: "#F1F5F9" }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Concerned Cinema Duty Personnel
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1E293B" }}>
                      {details.concerned_personnel_name || "None designated"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600, mb: 0.5 }}>
                      Personnel Observations / Notes
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        borderRadius: "4px",
                        p: 1.75,
                        fontSize: 13,
                        color: "#1E293B",
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {details.other_comment || "No additional comments recorded."}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Media Attachments & Evidence Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  Media Attachments & Evidence ({attachments.length})
                </Typography>

                {attachments.length === 0 ? (
                  <Box
                    sx={{
                      p: 3,
                      border: "1px dashed #CBD5E1",
                      borderRadius: "4px",
                      textAlign: "center",
                      color: "#94A3B8",
                      fontSize: 13,
                    }}
                  >
                    No photo or video attachments were uploaded with this inspection report.
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {imageAttachments.map((att: any, idx: number) => (
                      <Grid item xs={6} sm={4} key={idx}>
                        <Box
                          onClick={() => setZoomedImage(att.file_url)}
                          sx={{
                            position: "relative",
                            aspectRatio: "1",
                            borderRadius: "6px",
                            overflow: "hidden",
                            border: "1px solid #E2E8F0",
                            cursor: "pointer",
                            "&:hover .zoom-overlay": { opacity: 1 },
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={att.file_url}
                            alt="Attachment"
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                          <Box
                            className="zoom-overlay"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              backgroundColor: "rgba(0, 0, 0, 0.45)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: 0,
                              transition: "opacity 0.2s ease",
                              color: "#FFFFFF",
                              gap: 0.5,
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            <ZoomInRounded fontSize="small" />
                            <span>Zoom</span>
                          </Box>
                        </Box>
                      </Grid>
                    ))}

                    {videoAttachments.map((att: any, idx: number) => (
                      <Grid item xs={12} key={`video-${idx}`}>
                        <Box sx={{ p: 1.5, border: "1px solid #E2E8F0", borderRadius: "4px" }}>
                          <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600, mb: 1 }}>
                            Video Recording Attachment
                          </Typography>
                          <video
                            src={att.file_url}
                            controls
                            style={{ width: "100%", maxHeight: 280, borderRadius: "4px" }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Right Column: Resolution & Status Management */}
        <Grid item xs={12} lg={5}>
          <Stack spacing={3}>
            {/* Status & Resolution Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  Resolution & Status
                </Typography>

                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600, mb: 1 }}>
                    Change Report Status
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel id="status-select-label">Status</InputLabel>
                    <Select
                      labelId="status-select-label"
                      label="Status"
                      value={status}
                      onChange={handleStatusChange}
                      sx={{ borderRadius: "4px", fontSize: 13.5 }}
                    >
                      <MenuItem value="acknowledged">ACKNOWLEDGED (Official Action Taken)</MenuItem>
                      <MenuItem value="duplicate">DUPLICATE (Repeated Report)</MenuItem>
                      <MenuItem value="rejected">REJECTED (Insufficient Basis)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600, mb: 1 }}>
                    Official Administrative Remarks / Comments
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    placeholder="Enter official administrative feedback, resolution remarks, or compliance instructions for the inspector..."
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        fontSize: 13.5,
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 2.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                        sx={{ color: "#0072BC", "&.Mui-checked": { color: "#0072BC" } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 12.5, color: "#475569" }}>
                        Notify inspector via email ({deputy.email || "personnel email"})
                      </Typography>
                    }
                  />
                </Box>

                <LoadingButton
                  fullWidth
                  variant="contained"
                  loading={updating}
                  startIcon={<SendRounded fontSize="small" />}
                  onClick={handleUpdateStatusAndRemarks}
                  sx={{
                    backgroundColor: "#0072BC",
                    "&:hover": { backgroundColor: "#005a96" },
                    borderRadius: "4px",
                    textTransform: "none",
                    fontWeight: 650,
                    fontSize: 13.5,
                    py: 1,
                    boxShadow: "none",
                  }}
                >
                  Update Status & Send Remarks
                </LoadingButton>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PrintRounded fontSize="small" />}
                  onClick={() => handlePrintPDF(details)}
                  disabled={printLoading}
                  sx={{
                    mt: 1.5,
                    borderRadius: "4px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13.5,
                    py: 0.9,
                    borderColor: "#CBD5E1",
                    color: "#334155",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  {printLoading ? "Generating PDF..." : "Print PDF Report"}
                </Button>

                {details.admin && (
                  <Box
                    sx={{
                      mt: 2.5,
                      p: 1.5,
                      backgroundColor: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Avatar
                      sx={{ width: 34, height: 34, backgroundColor: "#0072BC", fontSize: 13 }}
                    >
                      {details.admin.name?.[0] || "A"}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                        Last Processed By
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>
                        {details.admin.name} {details.admin.last_name}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Lightbox Dialog for Image Zoom */}
      <Dialog
        open={Boolean(zoomedImage)}
        onClose={() => setZoomedImage(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "6px",
            overflow: "hidden",
            backgroundColor: "#000000",
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            onClick={() => setZoomedImage(null)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#FFFFFF",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.85)" },
            }}
          >
            <CloseRounded />
          </IconButton>
          {zoomedImage && (
            <img
              src={zoomedImage}
              alt="Zoomed Attachment"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "85vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </DefaultPage>
  );
}
